import { getRemoteConfigValue } from '../Firebase/firebaseFirestore'

// Remote Config key holding the native-support-chat org allowlist. Shape mirrors
// `dual_write_org_allowlist`: a JSON array of firestore_ids (or supabaseId UUIDs), or the
// string "*" to enable every org. Published via the backend admin SDK like the other RC flags.
const RC_KEY = 'native_support_chat_org_allowlist'

// Coerce a Remote Config value to a string across platforms (web returns a string; the native
// module returns a value object with `.asString()`). Mirrors dualWrite.ts `remoteConfigString`.
function rcString(key: string): string {
  try {
    const v = getRemoteConfigValue(key)
    if (typeof v === 'string') return v
    if (v && typeof v === 'object' && 'asString' in v && typeof (v as { asString?: unknown }).asString === 'function') {
      return String((v as { asString: () => unknown }).asString())
    }
    return ''
  } catch {
    return ''
  }
}

/**
 * Parse the native-support-chat allowlist. `*` ⇒ every org; otherwise only the listed ids.
 *
 * Fails CLOSED (no orgs) on unset / empty / malformed — unlike dual-write, which fails open in
 * dev. The native chat requires a migrated, active Supabase user (FirebaseAuthGuard); when an org
 * is not opted in, the help UI falls back to the web help center. An unloaded Remote Config
 * (cold start) therefore keeps that fallback until the real allowlist activates.
 */
export function parseSupportChatAllowlist(): { all: boolean; ids: Set<string> } {
  const raw = rcString(RC_KEY).trim()
  if (raw === '*' || raw === '"*"') return { all: true, ids: new Set() }
  if (!raw) return { all: false, ids: new Set() }
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      if (parsed.includes('*')) return { all: true, ids: new Set() }
      return { all: false, ids: new Set(parsed.filter((x): x is string => typeof x === 'string')) }
    }
  } catch {
    // Malformed allowlist (typo) ⇒ fail closed: keep the web help-center fallback rather than
    // silently flipping every org onto the native chat.
  }
  return { all: false, ids: new Set() }
}

/** Whether the given org is opted into the native support chat (matched by firestore id or uuid). */
export function isOrgSupportChatEnabled(
  org: { id?: string; supabaseId?: string } | null | undefined,
): boolean {
  const { all, ids } = parseSupportChatAllowlist()
  if (all) return true
  if (!org) return false
  const fsId = typeof org.id === 'string' ? org.id : undefined
  const sbId = typeof org.supabaseId === 'string' ? org.supabaseId : undefined
  return Boolean((fsId && ids.has(fsId)) || (sbId && ids.has(sbId)))
}
